import { AdminLog, AdminType } from '@Types/Admin.types';
import {
  Accessory,
  Asset,
  AssetChange,
  AssetLog,
  AssetStatus,
  AssetTemplate,
  AssetType,
  Assignment,
  Location,
  Notification,
  TaxStatus,
  UserAssetHistory,
} from '@Types/Asset.types';
import { KGCompany } from '@Types/Company.types.ts';
import { DropdownOption, FilterOption, MultiDropOption, RadioOption } from '@Types/Form.types.ts';
import { EmailLocation, KGOffice } from '@Types/Office.types.ts';
import { PaginatedResponse } from '@Types/Response.types';
import {
  ASSET_DETAILS,
  DROPDOWN_DEFAULT,
  ELEMENT_TO_DELETE_TYPE,
  FIELD_NAMES,
  RECIPIENT_DROPDOWN_VALUES,
  REPORT_TYPE,
} from '@Utils/constants';
import { KGPortalUser } from '~/Types/User.type.ts';
import { getDateString } from './date.utils.ts';
import { getYesNoIcon } from './icon.utils.tsx';

export const formatAssetTag = (data: Asset) => {
  const { assetTag, assetTypeCode, taxStatus } = data;

  if (assetTag && assetTypeCode && taxStatus) {
    return `${assetTag} - ${assetTypeCode} ${taxStatus}`;
  }
  return '';
};

export const formatAssetTagInAsset = (data: Asset) => {
  const { assetTag, assetTypeCode, taxStatus } = data;

  if (assetTag && assetTypeCode && taxStatus) {
    return { ...data, assetTag: formatAssetTag(data) };
  }
  return data;
};

export const formatAllAssetTag = (allData: Asset[]) =>
  allData.map((asset) => ({ ...asset, assetTag: formatAssetTag(asset) }));

export const formatAssetStatusDropdown = (result: AssetStatus[]) => {
  const formatted = result.map((fetchedAssetStatus) => ({
    value: fetchedAssetStatus.id,
    label: fetchedAssetStatus.description,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);
  return formatted;
};

export const formatAssetTypeDropdown = (result: AssetType[]) => {
  const formatted = result.map((fetchedAssetType) => ({
    value: fetchedAssetType.id,
    label: fetchedAssetType.description,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);
  return formatted;
};

export const formatLocationDropdown = (result: Location[]) => {
  const formatted = result.map((fetchedLocation) => ({
    value: fetchedLocation.id,
    label: `${fetchedLocation.country} - ${fetchedLocation.name}`,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);
  return formatted;
};

export const formatTaxStatusDropdown = (result: TaxStatus[]) => {
  const formatted = result.map((fetchedTaxStatus) => ({
    value: fetchedTaxStatus.id,
    label: fetchedTaxStatus.description,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);
  return formatted;
};

export const formatAssetTemplateDropdown = (result: AssetTemplate[]) => {
  const formatted = result.map((assetTemplate) => ({
    value: assetTemplate.id,
    label: assetTemplate.name,
    isDisabled: false,
  }));

  formatted.unshift({ ...DROPDOWN_DEFAULT, isDisabled: false });
  return formatted;
};

export const formatAdminLogListData = (data: PaginatedResponse<AdminLog>) =>
  data.docs.map((adminLog) => ({
    ...adminLog,
    loggedAt: getDateString(adminLog.loggedAt),
  }));

export const formatScannerLogListData = (data: PaginatedResponse<AssetLog>) =>
  data.docs.map((assetLog) => ({
    ...assetLog,
    id: assetLog.assetId,
    loggedAt: getDateString(assetLog.loggedAt),
    logType:
      assetLog.logType === 'Check-In' ? (
        <i className='fa-duotone fa-solid fa-arrow-down-to-square' />
      ) : (
        <i className='fa-duotone fa-solid fa-arrow-up-from-square' />
      ),
  }));

export const formatNotificationData = (data: PaginatedResponse<Notification>) =>
  data.docs.map((notification: Notification) => ({
    ...notification,
    id: String(notification.asset),
    date: getDateString(notification.date) || '',
  }));

export const formatAssignmentsData = (data: Assignment[]) =>
  data.map((assignment: Assignment) => ({
    ...assignment,
    assignmentDate: getDateString(assignment.assignmentDate) || '',
  }));

export const formatAssetChangeData = (data: AssetChange[]) =>
  data.map((change: AssetChange) => ({
    ...change,
    createdAt: getDateString(change.createdAt) || '',
  }));

const formatAssetSection = (
  section: {
    fields: {
      label: string;
      key: string;
    }[];
  },
  assetDetails: Asset,
) => {
  const booleanKeys = ['sharedDevice', 'inOffice', 'insideRegime'];

  return section.fields.map((field) => {
    const value = assetDetails[field.key as keyof Asset];
    if (Array.isArray(value)) {
      return null;
    }

    if (booleanKeys.includes(field.key)) {
      return (
        <p key={field.key}>
          {field.label}: {getYesNoIcon(value as boolean)}
        </p>
      );
    }

    return (
      <p key={field.key}>
        {field.label}: {value || 'N/A'}
      </p>
    );
  });
};

export const formatAssetDetails = (styles: { [x: string]: string | undefined }, assetDetails: Asset) =>
  ASSET_DETAILS.map((section) => (
    <div key={section.title}>
      <hr className={`divider ${styles['asset-details__divider']}`} />
      <h3>{section.title}</h3>
      {formatAssetSection(section, assetDetails)}
    </div>
  ));

export const convertToNumber = (value: string | number | undefined): number | undefined =>
  value !== '' && !Number.isNaN(value) ? Number(value) : undefined;

export default formatAllAssetTag;

export const formatPeopleDropdown = (data: KGPortalUser[] | undefined) => {
  if (!data) {
    return [];
  }
  const formatted = data.map((person) => ({
    value: person.email ?? '',
    label: person.fullName ?? '',
  }));

  formatted.unshift(DROPDOWN_DEFAULT);
  return formatted;
};

export const formatCountryDropdown = (data: string[]) => {
  return data.map((d) => ({
    value: d,
    label: d,
  }));
};

export const formatRoles = (roles: AdminType[]) =>
  [DROPDOWN_DEFAULT].concat(
    roles.map((role: AdminType) => ({
      value: role.id,
      label: role.description,
      isDisabled: false,
    })),
  );

export const formatElementsToDelete = (
  elements: Asset[] | Accessory[],
  type: (typeof ELEMENT_TO_DELETE_TYPE)[keyof typeof ELEMENT_TO_DELETE_TYPE],
) =>
  elements.map((element) => ({
    id: element.id,
    type,
  }));

export const formatAssetsDropdown = (assets: Asset[]) => {
  const formatted = assets.map((asset, index) => ({
    value: asset.id,
    label: `[${index}] ${asset.assetTag || asset.rfid || asset.serialNumber || asset.id} - ${asset.assetType}`,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);

  return formatted;
};

export const formatAccessoriesDropdown = (accessories: Accessory[]) => {
  const formatted = accessories.map((accessory, index) => ({
    value: accessory.id,
    label: `[${index}] ${accessory.name}`,
  }));

  formatted.unshift(DROPDOWN_DEFAULT);

  return formatted;
};

export const applyRegex = (value: string, regex: RegExp) => value.replace(regex, '');

export const updateOptionsByName = (
  array: FilterOption[],
  updates: { [key: string]: (option: FilterOption) => FilterOption },
) =>
  array.map((item: FilterOption) => {
    const updater = updates[item.name];
    return updater ? updater(item) : item;
  });
export const formatFilterOptions = (
  options: {
    [REPORT_TYPE.ASSET]: FilterOption[];
    [REPORT_TYPE.LOG]: FilterOption[];
  },
  assetStatus: AssetStatus[],
  assetTypes: AssetType[],
  location: Location[],
  taxStatus: TaxStatus[],
): {
  ASSET: FilterOption[];
  ASSET_LOG: FilterOption[];
} => ({
  ASSET: updateOptionsByName(options.ASSET, {
    [FIELD_NAMES.ASSET_STATUS]: (option) => ({
      ...option,
      options: [...new Set(assetStatus.map((status: AssetStatus) => status.description))],
    }),
    [FIELD_NAMES.ASSET_TYPE]: (option) => ({
      ...option,
      options: [...new Set(assetTypes.map((type: AssetType) => type.description))],
    }),
    [FIELD_NAMES.OFFICE_LOCATION]: (option) => ({
      ...option,
      options: [...new Set(location.map((loc: Location) => loc.location))],
    }),
    [FIELD_NAMES.OFFICE_NAME]: (option) => ({
      ...option,
      options: [...new Set(location.map((loc: Location) => loc.name))],
    }),
    [FIELD_NAMES.TAX_STATUS]: (option) => ({
      ...option,
      options: [...new Set(taxStatus.map((status: TaxStatus) => status.description))],
    }),
  }),
  ASSET_LOG: updateOptionsByName(options.ASSET_LOG, {
    [FIELD_NAMES.OFFICE_NAME]: (option) => ({
      ...option,
      options: [...new Set(location.map((loc: Location) => loc.name))],
    }),
    [FIELD_NAMES.OFFICE_LOCATION]: (option) => ({
      ...option,
      options: [...new Set(location.map((loc: Location) => loc.location))],
    }),
  }),
});

export const formatFilterDropdown = (options: {
  [REPORT_TYPE.ASSET]: FilterOption[];
  [REPORT_TYPE.LOG]: FilterOption[];
}): {
  [REPORT_TYPE.ASSET]: DropdownOption[];
  [REPORT_TYPE.LOG]: DropdownOption[];
} => ({
  [REPORT_TYPE.ASSET]: [
    DROPDOWN_DEFAULT,
    ...options.ASSET.map((option) => ({
      label: option.label,
      value: option.name,
    })),
  ],
  [REPORT_TYPE.LOG]: [
    DROPDOWN_DEFAULT,
    ...options.ASSET_LOG.map((option) => ({
      label: option.label,
      value: option.name,
    })),
  ],
});

export const formatFilterResults = (
  data: {
    [key: string]: string[] | boolean | { startDate: Date; endDate: Date } | undefined;
  },
  options: FilterOption[],
): {
  label: string;
  value:
    | boolean
    | string[]
    | {
        startDate: Date;
        endDate: Date;
      }
    | undefined;
  name: string;
}[] =>
  Object.keys(data)
    .map((key) => {
      const label = options.find((option) => option.name === key)?.label || '';
      return {
        label,
        value: data[key],
        name: key,
      };
    })
    .toReversed();

export function sortDescendingArrayByDate<T>(arr: T[], dateProp: keyof T): T[] {
  return [...arr].sort((a, z) => Date.parse(z[dateProp] as string) - Date.parse(a[dateProp] as string));
}

export const getAssignedEmployeeEmailOfAccessory = (accesory?: Accessory, assignmentId?: string): string => {
  if (accesory) {
    const { employeeEmail } = accesory.assignments.filter((assignment) => assignment.id === assignmentId)[0];
    return employeeEmail || '';
  }
  return '';
};

export const formatCompanyRadioOptions = (companies: KGCompany[]): RadioOption[] =>
  companies.map((company) => ({
    label: company.Name,
    key: company.CompanyID,
  }));

export const formatOfficeRadioOptions = (offices: KGOffice[]): RadioOption[] =>
  offices.map((office) => ({
    label: office.Name!,
    key: office.OfficeID,
  }));

export const formatAssetLocationRadioOptions = (locations: Location[]): RadioOption[] => {
  return locations.map((location) => ({
    label: location.name,
    key: location.name,
  }));
};
export const formatAssetTypeRadioOptions = (assetTypes: AssetType[]): RadioOption[] => {
  return assetTypes.map((assetType) => ({
    label: assetType.description,
    key: assetType.description,
  }));
};

export const formatAssetStatusRadioOptions = (assetStatuses: AssetStatus[]): RadioOption[] => {
  return assetStatuses.map((assetStatus) => ({
    label: assetStatus.description,
    key: assetStatus.description,
  }));
};

export const formatAssetHistoryForUser = (rawAssetHistory: Asset[], email: string): UserAssetHistory[] => {
  const history: UserAssetHistory[] = [];
  for (const asset of rawAssetHistory) {
    /*
                from backend, assignments.come in descending order
                i.e index 0 has most recent assignment

                Since this is asset history, if an assignment doesnt have employee email. should follow pattern of { unassignment => assignment } ...
                */
    const { assignments } = asset;

    let curAssignmentIndex = 0;
    while (curAssignmentIndex < assignments.length) {
      // Asset is currently assigned
      if (assignments[curAssignmentIndex].employeeEmail !== undefined) {
        // increment index by 1 because there is no un-assignment
        curAssignmentIndex++;
        continue;
      }
      const unassignment = assignments[curAssignmentIndex];

      const assignment = assignments[curAssignmentIndex + 1];
      if (assignment.employeeEmail === email) {
        const newAssetHistory: UserAssetHistory = {
          // id used for navigation
          // key used for list key in ListCard
          id: asset.id,
          key: unassignment.id,
          assignmentDate: getDateString(assignment.assignmentDate)!,
          unassignmentDate: getDateString(unassignment.assignmentDate)!,
          assetModel: asset.assetModel,
          serialNumber: asset.serialNumber,
        };
        history.push(newAssetHistory);
      }
      // Increment by 2 since unassignment and assignment comes in pairs
      curAssignmentIndex += 2;
    }
  }
  return history;
};

export const formatLocations = (locations: KGOffice[] | undefined): MultiDropOption<number>[] => {
  if (!locations) {
    return [];
  }

  return locations.map((value) => ({
    label: value.Name!,
    value: value.OfficeID,
    isDisabled: false,
    tooltip: value.Name!,
  }));
};

export const formatRecipients = (locations: EmailLocation[] | undefined): Map<number, MultiDropOption<string>[]> => {
  if (!locations) {
    return new Map();
  }

  const map = new Map();
  locations.forEach((location) => {
    const values = RECIPIENT_DROPDOWN_VALUES.filter((dropdown) =>
      location.Recipients.some((recipient) => recipient === dropdown.value),
    );
    map.set(location.OfficeID, values);
  });

  return map;
};
